import { useEffect, useState } from 'react';
import { InfoToggle } from '../components/InfoToggle';
import { Unavailable } from '../components/Unavailable';

interface Props {
  apiUrl: string;
}

interface Validator {
  did?: string;
  total_stake?: number | string;
  self_stake?: number | string;
  active?: boolean;
  jailed?: boolean;
  consensus_pubkey?: string;
  [k: string]: unknown;
}

type State =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'ok'; validators: Validator[] }
  | { kind: 'error' };

export function ValidatorsPanel({ apiUrl }: Props) {
  const [state, setState] = useState<State>({ kind: 'idle' });

  const load = async () => {
    setState({ kind: 'loading' });
    try {
      const res = await fetch(`${apiUrl}/validators`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const raw = body?.data ?? body?.validators ?? body;
      const validators: Validator[] = Array.isArray(raw) ? raw : [];
      setState({ kind: 'ok', validators });
    } catch {
      setState({ kind: 'error' });
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);

  const fmtStake = (n: number | string | undefined) => {
    if (n === undefined) return '—';
    const will = Math.round(Number(n) / 1e18);
    return `${will.toLocaleString()} WILL`;
  };

  return (
    <section className="panel">
      <h2>
        Validators
        <InfoToggle>
          Lists active validators from <code>GET /validators</code>. These are the BFT consensus
          participants whose signatures are verified by the CometBFT light client in the SDK.
        </InfoToggle>
      </h2>

      {state.kind === 'loading' && <p className="subtle">Loading…</p>}

      {state.kind === 'error' && (
        <Unavailable onRetry={load} testid="validators-error">
          Validators aren't available right now.
        </Unavailable>
      )}

      {state.kind === 'ok' && (
        <>
          <p className="subtle">
            {state.validators.length} validator{state.validators.length === 1 ? '' : 's'}
          </p>
          {state.validators.length === 0 ? (
            <p className="subtle">No validators returned.</p>
          ) : (() => {
            const totalNetworkStake = state.validators.reduce(
              (sum, v) => sum + Number(v.total_stake ?? 0),
              0,
            );
            const fmtPubkey = (k: string | undefined) =>
              k && k.length > 14 ? `${k.slice(0, 8)}…${k.slice(-4)}` : (k ?? '—');
            const fmtPower = (v: Validator) => {
              if (totalNetworkStake <= 0) return '—';
              return `${((Number(v.total_stake ?? 0) / totalNetworkStake) * 100).toFixed(1)}%`;
            };
            return (
              <table className="subgrove-table">
                <thead>
                  <tr>
                    <th>DID</th>
                    <th>Consensus key</th>
                    <th>Stake</th>
                    <th>Voting power</th>
                    <th>Self stake</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {state.validators.map((v, i) => (
                    <tr key={`${v.did ?? i}`}>
                      <td>
                        <code>{v.did ?? '—'}</code>
                      </td>
                      <td>
                        {v.consensus_pubkey ? (
                          <span className="copyable-cell">
                            <code title={v.consensus_pubkey}>{fmtPubkey(v.consensus_pubkey)}</code>
                            <button
                              type="button"
                              className="copy-btn"
                              title="Copy consensus key"
                              aria-label="Copy consensus key"
                              onClick={() => navigator.clipboard.writeText(v.consensus_pubkey!)}
                            >
                              ⧉
                            </button>
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{fmtStake(v.total_stake)}</td>
                      <td>{fmtPower(v)}</td>
                      <td>{fmtStake(v.self_stake)}</td>
                      <td>
                        {v.jailed ? (
                          <span className="muted">Jailed</span>
                        ) : v.active ? (
                          <span className="plan-badge plan-free">Active</span>
                        ) : (
                          <span className="muted">Inactive</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })()}
        </>
      )}
    </section>
  );
}
